import Loader from "../Loader/Loader";
import QAListItem from "./QAListItem";
import { useEffect, useRef } from "react";
import queryString from 'query-string';
import { useDispatch, useSelector } from "react-redux";
import { disableQuestions, getQnaList } from "../../../core/actionCreators/qnaActionCreator";
import { GET_QNA_DEFAULT_SIZE } from "../../../utils/constants";
import CommonPagination from "../../components/CommonPagination/CommonPagination";
import { publishQuestions } from "../../../core/actionCreators/qnaActionCreator";
import { toast } from "react-hot-toast";
import { removeEmptyStringKeys } from "../../../utils/helper";
import { useState } from "react";

const FILTER_DEBOUNCE_MS = 600;

const INPUT_CLASS = "border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full";

const QA_STATUSES = ["UNANSWERED", "ANSWERED", "PUBLISHED", "DISABLED"];

const TABLE_HEADERS = ["", "Q ID", "MSN", "Customer ID", "Category", "Question", "Status", "Add On Date", "Modified Date", "Action"];

const QAListData = (props: any) => {
    const dispatch = useDispatch();
    const { qnaList, isLoading } = useSelector((s: any) => s.qnaStore)
    const { user } = useSelector((s: any) => s.authStore)
    const [selectedQuestionIds, setSelectedQuestionIds] = useState([] as any);
    const { searchCriteria, setSearchCriteria } = props

    // Typing should settle before firing a request; paging should not wait.
    const isFilterChangeRef = useRef(false);

    useEffect(() => {
        const delay = isFilterChangeRef.current ? FILTER_DEBOUNCE_MS : 0;
        const timeout = setTimeout(() => {
            isFilterChangeRef.current = false;
            dispatch(getQnaList(queryString.stringify(removeEmptyStringKeys(searchCriteria)), null));
        }, delay);
        return () => clearTimeout(timeout);
    }, [searchCriteria, dispatch]);

    // A changed filter re-runs the search from the first page — keeping the old
    // page number would land the user on an empty tail of a shorter result set.
    const onFilterChange = (key: string, value: string) => {
        isFilterChangeRef.current = true;
        setSearchCriteria({ ...searchCriteria, [key]: value, pageNumber: 0 });
    };

    const hasActiveFilters = ["id", "productMsn", "customerId", "categoryName", "status"]
        .some((key) => !!searchCriteria[key]);

    const clearFilters = () => {
        isFilterChangeRef.current = false;
        setSearchCriteria({
            ...searchCriteria,
            id: "", productMsn: "", customerId: "", categoryName: "", status: "",
            pageNumber: 0,
        });
    };

    // CommonPagination counts pages from 1, the API counts them from 0.
    const onPageChange = (pageNumber: any) => {
        isFilterChangeRef.current = false;
        setSearchCriteria({ ...searchCriteria, pageNumber: Math.max(0, Number(pageNumber) - 1) })
    }

    const refreshList = () => {
        dispatch(getQnaList(queryString.stringify(removeEmptyStringKeys({ ...searchCriteria, pageNumber: 0 })), null));
    };

    const publishEventHandler = () => {
        if (selectedQuestionIds.length === 0) {
            toast.error("Fields are empty.");
            return;
        }

        const hasPublishedQuestions = selectedQuestionIds.some((item: { published: string; }) => item.published === 'PUBLISHED');

        if (hasPublishedQuestions) {
            setSelectedQuestionIds([]);
            toast.error("Already published question.")
            return;
        }

        const requestPayload = {
            cmsUserId: user.id || 33,
            questionIdList: selectedQuestionIds.map((item: { questionId: any; }) => item.questionId)
        };

        const callback = (r: any) => {
            setSelectedQuestionIds([]);
            toast.success("Questions published successfully.");
            if (!r) {
                refreshList();
            }
        };
        dispatch(publishQuestions(requestPayload, callback));
    };

    const disableEventHandler = () => {
        if (selectedQuestionIds.length === 0) {
            toast.error("Fields are empty.");
            return;
        }

        const requestPayload = {
            cmsUserId: user.id || 33,
            questionIdList: selectedQuestionIds.map((item: { questionId: any; }) => item.questionId)
        };

        const callback = (r: any) => {
            setSelectedQuestionIds([]);
            toast.success("Questions disabled successfully.");
            if (!r) {
                refreshList();
            }
        };
        dispatch(disableQuestions(requestPayload, callback));
    };

    return (
        <>
            <div className="mt-2.5 bg-white px-4 py-3 flex flex-wrap items-end gap-x-4 gap-y-3">
                <div className="w-[110px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Q ID</label>
                    <input
                        type="text"
                        placeholder="Q ID"
                        value={searchCriteria.id}
                        onChange={(e) => onFilterChange("id", e.target.value)}
                        className={INPUT_CLASS}
                    />
                </div>
                <div className="w-[200px]">
                    <label className="block text-[11px] text-gray-600 mb-1">MSN</label>
                    <input
                        type="text"
                        placeholder="Search MSN"
                        value={searchCriteria.productMsn}
                        onChange={(e) => onFilterChange("productMsn", e.target.value)}
                        className={INPUT_CLASS}
                    />
                </div>
                <div className="w-[160px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Customer ID</label>
                    <input
                        type="text"
                        placeholder="Customer ID"
                        value={searchCriteria.customerId}
                        onChange={(e) => onFilterChange("customerId", e.target.value)}
                        className={INPUT_CLASS}
                    />
                </div>
                <div className="w-[180px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Category</label>
                    <input
                        type="text"
                        placeholder="Search category"
                        value={searchCriteria.categoryName}
                        onChange={(e) => onFilterChange("categoryName", e.target.value)}
                        className={INPUT_CLASS}
                    />
                </div>
                <div className="w-[160px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Status</label>
                    <select
                        value={searchCriteria.status}
                        onChange={(e) => onFilterChange("status", e.target.value)}
                        className="bg-white border border-gray-300 h-8 w-full focus:outline-none focus:border-blue-400 px-1.5 rounded-sm text-xs"
                    >
                        <option value="">All</option>
                        {QA_STATUSES.map((status) => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>
                </div>
                <button
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    className="h-8 px-4 rounded text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Clear Filters
                </button>
            </div>

            {/* Bulk actions sit above the rows they act on, and say how many are
                selected so the buttons are not fired blind. */}
            <div className="mt-2.5 bg-white px-4 py-3 flex items-center gap-4">
                <span className="text-xs text-[#5E5D5D]">
                    {selectedQuestionIds.length > 0
                        ? `${selectedQuestionIds.length} question${selectedQuestionIds.length === 1 ? "" : "s"} selected`
                        : "Select questions to publish or disable"}
                </span>
                <button
                    onClick={publishEventHandler}
                    disabled={selectedQuestionIds.length === 0}
                    className="h-8 px-5 ml-auto rounded text-xs font-semibold bg-blue-500 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Publish
                </button>
                <button
                    onClick={disableEventHandler}
                    disabled={selectedQuestionIds.length === 0}
                    className="h-8 px-5 rounded text-xs font-semibold bg-blue-500 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Disable
                </button>
            </div>

            <div className="mt-2.5 campaign-list bg-white mb-[54px]">
                <table className="w-full">
                    <thead className="bg-[#343434]">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[4%]"></th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[7%]">Q ID</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[13%]">MSN</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[10%]">Customer ID</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[13%]">Category</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[22%]">Question</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[10%]">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[10%]">Add On Date</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[10%]">Modified Date</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[6%]">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={TABLE_HEADERS.length} className="py-12">
                                    <div className="flex flex-col items-center justify-center space-y-2">
                                        <Loader />
                                        <span className="text-xs text-gray-500">Loading questions...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            <QAListItem
                                selectedQuestionIds={selectedQuestionIds}
                                setSelectedQuestionIds={setSelectedQuestionIds}
                                data={qnaList?.qlist || []}
                            />
                        )}
                    </tbody>
                </table>
            </div>
            {qnaList && <CommonPagination data={qnaList} PAGE_SIZE={GET_QNA_DEFAULT_SIZE} onPageChange={onPageChange} />}
        </>
    )
}
export default QAListData;
